import { Component, OnDestroy, OnInit } from '@angular/core';
import { MethodDetails } from '../../../shared/ontology/model/method-details';
import { MethodSearchRequest } from '../../../shared/ontology/model/method-search-request.model';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { JhiEventManager, JhiLanguageService } from '../../../shared/core-utils';
import { MethodService } from '../../../shared/ontology/service/method.service';
import { FilterType } from '../../../shared/column-filter/column-filter.component';
import { ColumnFilterTransitionEventModel } from '../../../shared/column-filter/column-filter-transition-event.model';
import { MatchType } from '../../../shared/column-filter/column-filter-text-with-match-options-component';
import { SORT_PREDICATE_NONE } from '../../../germplasm-manager/germplasm-search-resolve-paging-params';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { Variable } from '../../../shared/ontology/model/variable';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'jhi-methods-pane',
    templateUrl: 'methods-pane.component.html'
})
export class MethodsPaneComponent implements OnInit, OnDestroy {
    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';
    protected readonly NAME_TEXT_LIMIT = 200;
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;

    COLUMN_FILTER_EVENT_NAME = MethodsPaneComponent.COLUMN_FILTER_EVENT_NAME;

    itemsPerPage = 20;

    ColumnLabels = ColumnLabels;

    methods: MethodDetails[];
    searchRequest: MethodSearchRequest;

    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;

    isLoading: boolean;
    methodFilters: any;

    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private methodService: MethodService) {
        this.page = 1;
        this.totalItems = 0;
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
        this.searchRequest = new MethodSearchRequest();
    }

    get filters() {
        return this.methodFilters;
    }

    set filters(filters) {
        this.methodFilters = filters;
    }

    get request() {
        return this.searchRequest;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.filters = this.getInitialFilters();
        this.registerColumnFiltersChanged();
        this.registerEvents();
        this.loadAll(this.request);
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                sort: this.getSort()
            },
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    registerEvents(): any {
        this.subscriptionsManager.add = this.eventManager.subscribe('methodPaneViewChanged', (event) => {
            this.loadAll(this.request);
        });
    }

    loadAll(request: MethodSearchRequest) {
        this.isLoading = true;
        this.methodService.searchMethods(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<Variable[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    trackId(index: number, item: MethodDetails) {
        return item.id;
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(MethodsPaneComponent.COLUMN_FILTER_EVENT_NAME, (event: ColumnFilterTransitionEventModel) => {
            this.resetTable();
        });
    }

    private getInitialFilters() {
        const me = this;
        return [
            {
                key: 'nameFilter', name: 'column-filter.method.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            {
                key: 'description', name: 'column-filter.description', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT
            }
        ];
    }

    private clearSort() {
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
    }

    private onSuccess(data: MethodDetails[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.methods = data;
    }
}

export enum ColumnLabels {
    'NAME' = 'methodName',
    'DESCRIPTION' = 'methodDefinition'
}
